import { fraunces } from '../../Landing/fonts';
import {
  Accent,
  DisplayHeading,
  Eyebrow,
  INK,
  MUTED,
  Reveal,
} from '../../Landing/ui';

const STEPS = [
  {
    title: 'Empty your camera roll',
    text: 'Five photos minimum, fifteen for a good article. Straight from your phone or camera, so the times and the GPS come with them. The whole post gets built around that timeline.',
  },
  {
    title: 'Answer what only you can',
    text: 'It reads your photos, then asks about what they can’t show: what you paid, what went wrong, who you met, what you’d tell someone to skip. Keep a trip diary? Paste it and most answers fill themselves in.',
  },
  {
    title: 'Pick the angle',
    text: 'You get a few article ideas your material can actually carry, each with the search it goes after. Choose one, rearrange the sections, cut the ones you don’t want.',
  },
  {
    title: 'Read it before anyone else does',
    text: 'The draft lands in your editor with your photos placed and captioned. Where a fact is missing you get a flag, and anything it checked on the web comes with its source for you to accept or reject. Nothing publishes itself.',
  },
];

// Real questions from the interview step — shown verbatim so the effort the
// tool asks for is visible before anyone signs up.
const QUESTIONS = [
  {
    q: 'Where did you sleep, and what did a night cost?',
    why: 'The name and the real price are what readers plan around.',
  },
  {
    q: 'What did you get wrong that you’d warn someone about?',
    why: 'One honest mistake proves this was a real trip.',
  },
  {
    q: 'Was anything not worth the time or the money?',
    why: 'Saying what to skip earns more trust than another highlight.',
  },
  {
    q: 'Did you also visit any of these five places?',
    why: 'Checks the guide’s breadth without inventing visits.',
  },
];

// Four stops, and the interview itself in a postcard frame — the questions
// are the product, so they are shown rather than described.
export default function AiFlightPlan() {
  return (
    <section id="how" className="tw-scroll-mt-20 tw-py-16 sm:tw-py-24">
      <div className="tw-mx-auto tw-max-w-7xl tw-px-4 sm:tw-px-6 lg:tw-px-8">
        <Reveal className="tw-max-w-2xl">
          <Eyebrow>The flight plan</Eyebrow>
          <DisplayHeading className="tw-text-3xl sm:tw-text-4xl lg:tw-text-[2.75rem]">
            How it works: four stops, and <Accent>you fly</Accent> two.
          </DisplayHeading>
          <p className="tw-mb-0 tw-mt-4 tw-max-w-xl tw-text-[15px] tw-leading-relaxed tw-text-[#5F6F69] dark:tw-text-[#93A69E]">
            Most AI writing assistants hand you a travel post in seconds. This
            one asks for the trip first, so expect about fifteen minutes on your
            side of it. That&apos;s the part no model can do for you, and
            it&apos;s the reason the post is worth reading.
          </p>
        </Reveal>

        <div className="tw-mt-12 tw-grid tw-grid-cols-1 tw-items-center tw-gap-12 lg:tw-mt-16 lg:tw-grid-cols-[0.9fr_1.1fr] lg:tw-gap-16">
          {/* The stops */}
          <Reveal>
            <ol className="tw-m-0 tw-list-none tw-p-0">
              {STEPS.map((step, i) => (
                <li key={step.title} className="tw-relative tw-flex tw-gap-5">
                  {/* Stop marker + dashed leg down to the next stop */}
                  <div className="tw-flex tw-flex-col tw-items-center">
                    <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-dashed tw-border-primary-600/50 dark:tw-border-primary-300/50 tw-bg-primary-600/5 dark:tw-bg-primary-300/10 tw-font-mono tw-text-[12px] tw-font-bold tw-text-primary-700 dark:tw-text-primary-300">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {i < STEPS.length - 1 && (
                      <span
                        aria-hidden="true"
                        className="tw-my-1 tw-w-0 tw-flex-1 tw-border-0 tw-border-l-2 tw-border-dashed tw-border-[#C9C0AA] dark:tw-border-[#3A4D44]"
                      />
                    )}
                  </div>
                  <div className={i < STEPS.length - 1 ? 'tw-pb-9' : ''}>
                    <h3
                      className={`${fraunces.className} ${INK} tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight sm:tw-text-[1.35rem]`}
                    >
                      {step.title}
                    </h3>
                    <p
                      className={`${MUTED} tw-mb-0 tw-mt-2 tw-max-w-md tw-text-[15px] tw-leading-relaxed`}
                    >
                      {step.text}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </Reveal>

          {/* The interview, verbatim, in a postcard frame */}
          <Reveal delay={0.15}>
            <figure className="tw-relative tw-m-0 tw-rotate-[1.2deg] tw-rounded-md tw-bg-white dark:tw-bg-[#EFEAE0] tw-p-3 tw-pb-10 tw-shadow-[0_30px_60px_-20px_rgba(29,45,40,0.35)] tw-transition-transform tw-duration-500 hover:tw-rotate-[0.3deg]">
              <div className="tw-rounded-sm tw-bg-[#10211B] tw-p-6 sm:tw-p-8">
                <p className="tw-m-0 tw-font-mono tw-text-[10px] tw-font-semibold tw-tracking-[0.22em] tw-text-primary-300">
                  THE INTERVIEW · 9 QUESTIONS
                </p>
                <p
                  className={`${fraunces.className} tw-mb-0 tw-mt-3 tw-text-xl tw-font-semibold tw-leading-snug tw-text-[#EBE6D9] sm:tw-text-2xl`}
                >
                  It asks. You answer. Then it writes.
                </p>
                <ul className="tw-m-0 tw-mt-6 tw-list-none tw-space-y-4 tw-p-0">
                  {QUESTIONS.map(item => (
                    <li
                      key={item.q}
                      className="tw-border-0 tw-border-l tw-border-solid tw-border-primary-300/40 tw-pl-4"
                    >
                      <p className="tw-m-0 tw-text-[14px] tw-leading-snug tw-text-[#EBE6D9]">
                        {item.q}
                      </p>
                      <p className="tw-mb-0 tw-mt-1 tw-text-[12.5px] tw-leading-snug tw-text-[#EBE6D9]/55">
                        {item.why}
                      </p>
                    </li>
                  ))}
                </ul>
                <p className="tw-mb-0 tw-mt-6 tw-font-mono tw-text-[10.5px] tw-leading-relaxed tw-tracking-wide tw-text-[#EBE6D9]/50">
                  Skip any of them and the article just gets thinner. Skip all
                  of them and there&apos;s nothing to write.
                </p>
              </div>
              <figcaption className="tw-flex tw-items-baseline tw-justify-between tw-px-1 tw-pt-3 tw-font-mono tw-text-[10px] tw-tracking-[0.22em] tw-text-[#5F6F69]">
                <span>AI BLOGGER · STEP 2 OF 4</span>
                <span className="tw-hidden sm:tw-inline">~15 MIN</span>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
